/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/require-default-props */
import React, { FC } from 'react'
import {
  Button,
  Typography,
  styled,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { colors, typography, Link } from '@resiliatech/birdsong'
import { DonAItions } from 'Components/Pages/DonationPortal/OptimizationModal/svg'
import { useQuery } from '@apollo/client'
import { GOALS } from 'Apollo/Queries/Goals'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  AiGoalsStoryInput,
  AiStoryInput,
  StoryVariants,
  VariantSpecificData,
} from '../StoryAiTypes'
import { StoryAiGoalInput } from './StoryAiGoalInput'
import { StoryAiInput } from './StoryAiInput'

type Props = {
  aiInput: AiGoalsStoryInput | AiStoryInput
  handleFocusChange: (input: string) => void
  handleCheckboxChange: (checked: boolean) => void
  handleSubmit: () => void
  handleChange: (value: string, key: string) => void
  storyVariant: StoryVariants
  variantSpecificData?: VariantSpecificData
}

export const StoryAiInputContainer: FC<Props> = ({
  handleSubmit,
  handleChange,
  handleFocusChange,
  handleCheckboxChange,
  aiInput,
  storyVariant,
  variantSpecificData,
}) => {
  const methods = useForm()
  const {
    nonprofitOrgConfig: { orgId },
  } = useAppState()
  // may be extracted to a util function in the future
  // purpose and audience are required for both variants
  let isDisabled = !aiInput.purpose || !aiInput.audience
  if (!isDisabled) {
    if (storyVariant === StoryVariants.STORY) {
      const typedAiInput = aiInput as AiStoryInput
      isDisabled = !typedAiInput.impact || !typedAiInput.importance
    } else {
      const typedAiInput = aiInput as AiGoalsStoryInput
      isDisabled = !typedAiInput?.goal
    }
  }

  // IF NO GOALS WERE PROVIDED, WE NEED TO QUERY FOR THEM
  // GOALS SHOULD BE PROVIDED IF THE DIALOG IS OPENED FROM A GOAL
  const { data: { goals: goalsData = [] } = {} } = useQuery(GOALS, {
    variables: { activeOnly: true, nonprofitOrgId: orgId },
    skip: !orgId || !!variantSpecificData?.goal?.goalArray,
    onCompleted: (data) => {
      // IF THE USER HAS NO GOALS, WE UNCHECK THE CHECKBOX
      if (data.goals.length === 0) {
        handleCheckboxChange(false)
      }
    },
  })

  const hasGoals = !!variantSpecificData?.goal?.goalArray || !!goalsData.length

  const CheckboxLabel = (
    <div>
      {`Use the progress I've made in `}
      <Link underline='always' href='/impact/goals' target='_blank'>
        Goals
      </Link>
    </div>
  )

  return (
    <InputSection>
      {!hasGoals && <Subtitle>Describe your impact</Subtitle>}
      <FormProvider {...methods}>
        {/* CURRENTLY, BOTH StoryAiInput AND StoryAiGoalInput USE THIS CHECKBOX
        IF/WHEN FUTURE VARIANTS ARE ADDED IT MAY MAKE SENSE TO REFACTOR
        -  WE HIDE THE INPUT IF THE USER HAS NO GOALS */}
        {hasGoals && (
          <FormControlLabel
            label={CheckboxLabel}
            control={
              <Checkbox
                data-testid='useGoalProgressCheckbox'
                defaultChecked={storyVariant === StoryVariants.GOALS}
                color='primary'
                onChange={(e) => handleCheckboxChange(e.target.checked)}
              />
            }
          />
        )}

        {storyVariant === StoryVariants.STORY && (
          <StoryAiInput
            handleChange={handleChange}
            handleFocusChange={handleFocusChange}
            aiInput={aiInput as AiStoryInput}
          />
        )}
        {storyVariant === StoryVariants.GOALS && (
          <StoryAiGoalInput
            handleChange={handleChange}
            handleFocusChange={handleFocusChange}
            aiInput={aiInput as AiGoalsStoryInput}
            goalArray={variantSpecificData?.goal?.goalArray || goalsData}
          />
        )}

        <ButtonArea>
          <StyledButton
            variant='contained'
            endIcon={<DonAItions />}
            onClick={handleSubmit}
            disabled={isDisabled}
          >
            Create Preview
          </StyledButton>
        </ButtonArea>
      </FormProvider>
    </InputSection>
  )
}

const Subtitle = styled(Typography)`
  ${typography.up1.semiBoldSans.styles}
`

const StyledButton = styled(Button)<{ disabled: boolean }>`
  align-self: flex-start;
  & svg {
    fill: ${({ disabled }) =>
      disabled ? 'rgba(0, 0, 0, 0.26)' : colors.copy.headerLight};
  }
`

const ButtonArea = styled('div')`
  display: flex;
  align-items: center;
  margin-top: 20px;
  gap: 12px;
`

const InputSection = styled('div')`
  background: ${colors.background.secondaryBackground};
  display: flex;
  flex-direction: column;
  padding: 20px;
  flex: 1;
  border-radius: 8px;
`
